import {
  Grid,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Box,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { AppContext } from "../../../store/AppContext";
import { CustomButton } from "../../../components/CustomButton/CustomButton";

const ViewCustomer = () => {
  const {
    orderTabInfo,
    customerTabInfo,
    setOrderTabInfo,
    setHeaderData,
    setSelectedTab,
  } = React.useContext(AppContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const [addresses, setAddresses] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAddress, setEditingAddress] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const customerId = customerTabInfo?.selectedCustomer?.id;

  // Fetch customer addresses
  const fetchAddresses = async () => {
    try {
      const addressesRef = collection(db, "users", customerId, "addresses");
      const querySnapshot = await getDocs(addressesRef);
      const addressesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAddresses(addressesData);
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  };

  // Fetch customer orders
  const fetchOrders = async () => {
    try {
      const ordersRef = collection(db, "users", customerId, "orders");
      const querySnapshot = await getDocs(ordersRef);
      const ordersData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setOrders(ordersData);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit button click
  const handleEditClick = (address) => {
    setEditingAddress(address.id);
    setEditFormData(address);
    setOpenDialog(true);
  };

  // Handle form input changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value,
    });
  };

  // Save edited address
  const handleSave = async () => {
    try {
      const addressRef = doc(
        db,
        "users",
        customerId,
        "addresses",
        editingAddress
      );
      await updateDoc(addressRef, editFormData);
      setOpenDialog(false);
      fetchAddresses(); // Refresh addresses
    } catch (error) {
      console.error("Error updating address:", error);
    }
  };

  const handleOrderClick = (order) => {
    setHeaderData((prevState) => ({
      ...prevState,
      leftText: "Order details",
      rightActionText: "Accept order",
      back: false,
      leftAction: () => {
        setOrderTabInfo((prevState) => ({
          ...prevState,
          viewOrder: false,
          selectedOrder: null,
        }));
        setHeaderData({
          leftText: "",
          rightActionText: "",
          leftAction: () => {},
          rightAction: () => {
            setOrderTabInfo((prevState) => ({
              ...prevState,
              trigger: true,
            }));
          },
        });
      },
    }));

    setOrderTabInfo((prevState) => ({
      ...prevState,
      viewOrder: true,
      selectedOrder: order,
    }));
    setSelectedTab({
      subCategory: { index: 0 },
      category: { title: "Orders", index: 2 },
    });
  };

  useEffect(() => {
    if (customerId) {
      setLoading(true);
      fetchAddresses();
      fetchOrders();
    }
  }, [customerId]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          fontSize: isMobile ? "14px" : "16px",
        }}
      >
        Loading customer data...
      </Box>
    );
  }

  const getStatusBackgroundColor = (status) => {
    switch (status?.toUpperCase()) {
      case "CREATED":
        return "rgba(59, 130, 246, 0.1)";
      case "PENDING":
        return "rgba(234, 179, 8, 0.1)";
      case "ACCEPTED":
        return "rgba(16, 185, 129, 0.1)";
      case "SHIPPED":
        return "rgba(139, 92, 246, 0.1)";
      case "REJECTED":
        return "rgba(239, 68, 68, 0.1)";
      case "COMPLETED":
        return "rgba(5, 150, 105, 0.1)";
      case "CANCELLED":
        return "rgba(107, 114, 128, 0.1)";
      default:
        return "rgba(59, 130, 246, 0.1)";
    }
  };

  const getStatusTextColor = (status) => {
    switch (status?.toUpperCase()) {
      case "CREATED":
        return "#3B82F6";
      case "PENDING":
        return "#EAB308";
      case "ACCEPTED":
        return "#10B981";
      case "SHIPPED":
        return "#8B5CF6";
      case "REJECTED":
        return "#EF4444";
      case "COMPLETED":
        return "#059669";
      case "CANCELLED":
        return "#6B7280";
      default:
        return "#3B82F6";
    }
  };

  // Mobile Address Card Component
  const AddressCard = ({ address }) => (
    <Card sx={{ height: "100%", position: "relative" }}>
      <CardContent sx={{ p: isMobile ? 2 : 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
          <LocationOnIcon sx={{ color: "#023DE1", mr: 1, mt: 0.5 }} />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              sx={{ fontWeight: 600, mb: 1 }}
            >
              {address.firstName} {address.lastName}
              {address.is_default && (
                <Chip
                  label="Default"
                  size="small"
                  color="success"
                  sx={{ ml: 1, fontSize: "0.75rem" }}
                />
              )}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => handleEditClick(address)}
            sx={{ ml: 1 }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ pl: isMobile ? 0 : 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {address.city}, {address.state} {address.pinCode}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {address.country}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Phone: {address.mobileNumber}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  // Mobile Order Card Component
  const OrderCard = ({ order }) => {
    const orderDate = order.timestamp?.seconds
      ? new Date(order.timestamp.seconds * 1000)
      : new Date();

    return (
      <Card
        sx={{
          cursor: "pointer",
          "&:hover": { boxShadow: 3 },
          transition: "box-shadow 0.2s",
        }}
        onClick={() => handleOrderClick(order)}
      >
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
            <ShoppingCartIcon sx={{ color: "#023DE1", mr: 1, mt: 0.5 }} />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant={isMobile ? "subtitle1" : "h6"}
                sx={{ fontWeight: 600, mb: 1 }}
              >
                Order #{order.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {orderDate.toLocaleDateString()},{" "}
                {orderDate.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {order?.quantity_each?.[0]?.images?.[0] && (
              <img
                style={{
                  width: isMobile ? "60px" : "80px",
                  height: isMobile ? "60px" : "80px",
                  borderRadius: "8px",
                  objectFit: "cover",
                  flexShrink: 0,
                }}
                src={order.quantity_each[0].images[0]}
                alt="Product"
              />
            )}

            <Box sx={{ flex: 1 }}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {order.quantity_each?.length || 0} ITEMS
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  ₹{order.items_total?.toFixed(2) || "0.00"}
                </Typography>
              </Box>

              <Chip
                label={order.status || "CREATED"}
                size="small"
                sx={{
                  backgroundColor: getStatusBackgroundColor(order.status),
                  color: getStatusTextColor(order.status),
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        width: "95%",
        alignItems: "start",
        justifyContent: "center",
        margin: "auto",
        padding: isMobile ? "16px 8px" : "20px 0",
      }}
    >
      {/* Addresses Section */}
      <div style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 500 }}>
            Addresses
          </Typography>
        </div>

        {addresses.length === 0 ? (
          <Card sx={{ p: 3, textAlign: "center" }}>
            <LocationOnIcon
              sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="body1" color="textSecondary">
              No addresses found for this customer
            </Typography>
          </Card>
        ) : (
          <Grid container spacing={isMobile ? 2 : 3}>
            {addresses.map((address) => (
              <Grid
                item
                xs={12}
                sm={isMobile ? 12 : 6}
                md={isTablet ? 6 : 4}
                key={address.id}
              >
                {isMobile ? (
                  <AddressCard address={address} />
                ) : (
                  <div
                    style={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                      background: "white",
                      boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
                      borderRadius: "12px",
                      padding: isTablet ? "16px" : "20px",
                      position: "relative",
                    }}
                  >
                    <IconButton
                      style={{ position: "absolute", top: "8px", right: "8px" }}
                      onClick={() => handleEditClick(address)}
                      size={isTablet ? "small" : "medium"}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>

                    <h4
                      style={{
                        margin: 0,
                        fontWeight: "600",
                        fontSize: isTablet ? "16px" : "18px",
                      }}
                    >
                      {address.firstName} {address.lastName}
                      {address.is_default && (
                        <span
                          style={{
                            color: "green",
                            fontSize: isTablet ? "0.7rem" : "0.8rem",
                            marginLeft: "8px",
                            background: "#e8f5e9",
                            padding: "2px 8px",
                            borderRadius: "12px",
                          }}
                        >
                          Default
                        </span>
                      )}
                    </h4>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          color: "#555",
                          fontSize: isTablet ? "13px" : "14px",
                        }}
                      >
                        {address.streetAddress1}
                      </p>
                      {address.streetAddress2 && (
                        <p
                          style={{
                            margin: 0,
                            color: "#555",
                            fontSize: isTablet ? "13px" : "14px",
                          }}
                        >
                          {address.streetAddress2}
                        </p>
                      )}
                      <p
                        style={{
                          margin: 0,
                          color: "#555",
                          fontSize: isTablet ? "13px" : "14px",
                        }}
                      >
                        {address.city}, {address.state} {address.pinCode}
                      </p>
                      <p
                        style={{
                          margin: 0,
                          color: "#555",
                          fontSize: isTablet ? "13px" : "14px",
                        }}
                      >
                        {address.country}
                      </p>
                      <p
                        style={{
                          margin: 0,
                          color: "#555",
                          fontSize: isTablet ? "13px" : "14px",
                        }}
                      >
                        Phone: {address.mobileNumber}
                      </p>
                    </div>
                  </div>
                )}
              </Grid>
            ))}
          </Grid>
        )}
      </div>

      {/* Orders Section */}
      <div style={{ width: "100%" }}>
        <Typography
          variant={isMobile ? "h6" : "h5"}
          sx={{ fontWeight: 500, mb: 2 }}
        >
          Orders
        </Typography>

        {orders.length === 0 ? (
          <Card sx={{ p: 3, textAlign: "center" }}>
            <ShoppingCartIcon
              sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="body1" color="textSecondary">
              No orders found for this customer
            </Typography>
          </Card>
        ) : (
          <Grid container spacing={isMobile ? 2 : 3}>
            {orders.map((order) => (
              <Grid
                item
                xs={12}
                sm={isMobile ? 12 : 6}
                md={isTablet ? 6 : 5}
                key={order.id}
              >
                {isMobile ? (
                  <OrderCard order={order} />
                ) : (
                  (() => {
                    const orderDate = order.timestamp?.seconds
                      ? new Date(order.timestamp.seconds * 1000)
                      : new Date();

                    return (
                      <div
                        style={{
                          height: "100%",
                          background: "white",
                          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
                          borderRadius: "12px",
                          padding: isTablet ? "16px" : "20px",
                          display: "flex",
                          flexDirection: "column",
                          cursor: "pointer",
                          gap: "12px",
                        }}
                        onClick={() => handleOrderClick(order)}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <h4
                            style={{
                              margin: 0,
                              fontWeight: "600",
                              fontSize: isTablet ? "16px" : "18px",
                            }}
                          >
                            Order #{order.id}
                          </h4>
                          <p
                            style={{
                              margin: 0,
                              color: "#666",
                              fontSize: isTablet ? "12px" : "14px",
                            }}
                          >
                            {orderDate.toLocaleDateString()},{" "}
                            {orderDate.toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: isTablet ? "12px" : "16px",
                          }}
                        >
                          <img
                            style={{
                              width: isTablet ? "60px" : "80px",
                              height: isTablet ? "60px" : "80px",
                              borderRadius: "8px",
                              background: "#f5f5f5",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#999",
                              flexShrink: 0,
                              objectFit: "cover",
                              objectPosition: "top",
                            }}
                            src={order?.quantity_each?.[0]?.images?.[0]}
                            alt="Product"
                          />

                          <div
                            style={{
                              flexGrow: 1,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <p
                                style={{
                                  margin: 0,
                                  color: "#666",
                                  fontSize: isTablet ? "12px" : "14px",
                                }}
                              >
                                {order.quantity_each?.length || 0} ITEMS
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontWeight: "600",
                                  fontSize: isTablet ? "14px" : "16px",
                                }}
                              >
                                ₹{order.items_total?.toFixed(2) || "0.00"}
                              </p>
                            </div>

                            <div
                              style={{
                                marginTop: "8px",
                                display: "flex",
                                gap: "8px",
                              }}
                            >
                              <span
                                style={{
                                  background: getStatusBackgroundColor(
                                    order.status
                                  ),
                                  color: getStatusTextColor(order.status),
                                  padding: isTablet ? "3px 8px" : "4px 12px",
                                  borderRadius: "12px",
                                  fontSize: isTablet ? "0.75rem" : "0.875rem",
                                  fontWeight: "500",
                                }}
                              >
                                {order.status || "CREATED"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()
                )}
              </Grid>
            ))}
          </Grid>
        )}
      </div>

      {/* Edit Address Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: isMobile ? "1.25rem" : "1.5rem" }}>
          Edit Address
        </DialogTitle>
        <DialogContent style={{ paddingTop: "8px" }}>
          <TextField
            margin="dense"
            label="First Name"
            name="firstName"
            value={editFormData.firstName || ""}
            onChange={handleFormChange}
            fullWidth
            variant="outlined"
            size={isMobile ? "medium" : "small"}
          />
          <TextField
            margin="dense"
            label="Last Name"
            name="lastName"
            value={editFormData.lastName || ""}
            onChange={handleFormChange}
            fullWidth
            variant="outlined"
            size={isMobile ? "medium" : "small"}
          />
          <TextField
            margin="dense"
            label="Street Address 1"
            name="streetAddress1"
            value={editFormData.streetAddress1 || ""}
            onChange={handleFormChange}
            fullWidth
            variant="outlined"
            size={isMobile ? "medium" : "small"}
          />
          <TextField
            margin="dense"
            label="Street Address 2"
            name="streetAddress2"
            value={editFormData.streetAddress2 || ""}
            onChange={handleFormChange}
            fullWidth
            variant="outlined"
            size={isMobile ? "medium" : "small"}
          />
          <TextField
            margin="dense"
            label="City"
            name="city"
            value={editFormData.city || ""}
            onChange={handleFormChange}
            fullWidth
            variant="outlined"
            size={isMobile ? "medium" : "small"}
          />
          <TextField
            margin="dense"
            label="State"
            name="state"
            value={editFormData.state || ""}
            onChange={handleFormChange}
            fullWidth
            variant="outlined"
            size={isMobile ? "medium" : "small"}
          />
          <TextField
            margin="dense"
            label="Postal Code"
            name="pinCode"
            value={editFormData.pinCode || ""}
            onChange={handleFormChange}
            fullWidth
            variant="outlined"
            size={isMobile ? "medium" : "small"}
          />
          <TextField
            margin="dense"
            label="Country"
            name="country"
            value={editFormData.country || ""}
            onChange={handleFormChange}
            fullWidth
            variant="outlined"
            size={isMobile ? "medium" : "small"}
          />
          <TextField
            margin="dense"
            label="Mobile Number"
            name="mobileNumber"
            value={editFormData.mobileNumber || ""}
            onChange={handleFormChange}
            fullWidth
            variant="outlined"
            size={isMobile ? "medium" : "small"}
          />
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 1 }}>
          <Button
            color="error"
            onClick={() => setOpenDialog(false)}
            size={isMobile ? "large" : "medium"}
          >
            Cancel
          </Button>
          <CustomButton
            onClick={handleSave}
            variant="contained"
            color="primary"
            size={isMobile ? "large" : "medium"}
          >
            Save Changes
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ViewCustomer;
